<script>
import client from '../sanityConfig'
import imageUrlBuilder from '@sanity/image-url'
import { mapState, mapGetters } from 'vuex'
import HtmlOnly from '@components/html-only/html-only.vue'
import { breakpointsMixin } from '../mixins/breakpointsMixin'
import { fontMixin } from '../mixins/fontMixin'

const builder = imageUrlBuilder(client)
const blocksToHtml = require('@sanity/block-content-to-html')

export default {
  components: {
    HtmlOnly
  },
  mixins: [breakpointsMixin, fontMixin],
  computed: {
    ...mapState(['user', 'settings', 'fromChallenge']),
    ...mapGetters(['confirmation']),
    confirmationIndex () {
      const index = this.confirmation.findIndex((page) => `/${page.slug.current}` === this.$route.path)
      if (index === -1) {
        return this.confirmation.findIndex((page) => page.slug.current === 'confirmation')
      }
      return index
    },
    hasHtmlOnly () {
      if (this.selectedConfirmationComponent?.html?.htmlFile) {
        return this.selectedConfirmationComponent.html
      }
      return false
    },
    selectedConfirmationComponent () {
      return this.confirmation[this.confirmationIndex] && this.confirmation[this.confirmationIndex].components &&
        this.confirmation[this.confirmationIndex].components.length > 0 && this.confirmation[this.confirmationIndex].components[0] &&
        (this.$route && this.$route.name.includes('Challenge Confirmation')
          ? this.confirmation[this.confirmationIndex].components[0].challengeConfirmation
          : this.confirmation[this.confirmationIndex].components[0].subscriptionConfirmation)
    },
    confirmationHeaderText () {
      return this.selectedConfirmationComponent?.confirmationHeaderText?.text
    },
    confirmationDetailText () {
      return this.selectedConfirmationComponent && this.convertWysiwyg(this.selectedConfirmationComponent.confirmationDetailText.text)
    },
    justifyText () {
      return this.selectedConfirmationComponent?.justify
    },
    sideImage () {
      return this.selectedConfirmationComponent && this.selectedConfirmationComponent.sideImage
    },
    backgroundImage () {
      return this.selectedConfirmationComponent && this.selectedConfirmationComponent.backgroundImage
    },
    mobileBackgroundImage () {
      return this.selectedConfirmationComponent && this.selectedConfirmationComponent.mobileBackgroundImage
    },
    backgroundImageToShow () {
      if (this.isMobile || this.isTablet) {
        return this.mobileBackgroundImage
      }
      return this.backgroundImage
    },
    logo () {
      if (this.selectedConfirmationComponent && this.selectedConfirmationComponent.logo) {
        return this.urlFor(this.selectedConfirmationComponent.logo)
      }
      return this.urlFor(this.settings.icon)
    }
  },
  methods: {
    urlFor (source) {
      return builder.image(source)
    },
    convertWysiwyg (source) {
      return blocksToHtml({
        blocks: source
      })
    }
  }
}
</script>

<template>
  <div>
    <div
      v-if="!hasHtmlOnly"
      class='confirmation-container'
      :style="{'background-image': `url(${urlFor(backgroundImageToShow)})`,}"
    >
      <div
        class='content-wrapper'
        :style="{'--justify': justifyText}"
      >
        <a href="/"><img class="icon" :src="logo" /></a>
        <h1
          :style="$_getFontStyle(selectedConfirmationComponent, 'confirmationHeaderText')"
          class="confirmation-header"
        >
         {{ confirmationHeaderText }}
        </h1>
        <div
          v-html="confirmationDetailText"
          :style="$_getFontStyle(selectedConfirmationComponent, 'confirmationDetailText')"
          class="confirmation-detail"
        />
        <div class="app-store-icons-wrapper">
          <a v-if="settings.iosAppUrl" target="_blank" :href="settings.iosAppUrl">
            <img src="https://plankk.partners/wp-content/uploads/2022/10/Apple-Badge-ENG-Approved-1.svg"/>
          </a>
          <a v-if="settings.androidAppLink" target="_blank" :href="settings.androidAppLink">
            <img class="google-play" src="https://plankk.partners/wp-content/uploads/2022/10/Google-Badge-ENG-Approved-1.svg"/>
          </a>
        </div>
      </div>
      <img :src="urlFor(sideImage)" class="side-image"/>
    </div>
    <div v-if="hasHtmlOnly">
      <HtmlOnly :component="hasHtmlOnly" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
 .confirmation-container {
    font-family: "Nunito Sans", sans-serif;
    color: #212529;
    display: flex;
    background-repeat: no-repeat;
    background-size: cover;
    .content-wrapper {
      -webkit-font-smoothing: antialiased;
      color: #212529;
      margin: 60px;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: var(--justify) !important;
      text-align: var(--justify) !important;
        img {
          margin-top: 5%;
          &.icon {
            max-width: 80px;
          }
        }
        h1 {
          margin-top: 30px;
          color: #000;
          font-size: 3.3em;
          margin-bottom: 20px;
          font-weight: 800;
          font-family: "Nunito Sans", sans-serif;
        }
        .app-store-icons-wrapper {
          display: flex;
          a {
            img {
              cursor: pointer;
              &:hover { opacity: 0.7;}
            }
          }
          a + a {
            margin-left: 8px;
          }
        }
        .confirmation-header {
          font-family: var(--font-family) !important;
          font-weight: var(--font-weight) !important;
          font-size: var(--font-size) !important;
        }
        .confirmation-detail {
          font-family: var(--font-family) !important;
          font-weight: var(--font-weight) !important;
          font-size: var(--font-size) !important;
        }
      }
    .side-image {
      max-width: 100%;
    }
  }
  @media (max-width:1000px) {
    .confirmation-container {
      flex-direction: column;
    }
  }
</style>
